'use client';

import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { visitesService, locauxService, enseignesService } from '@/lib/supabase/services';
import type { Visite } from '@/lib/supabase/services';

const COLUMNS: { statut: Visite['statut']; label: string; color: string; bg: string; border: string }[] = [
  { statut: 'Planifiée', label: 'Planifiées', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
  { statut: 'Réalisée', label: 'Réalisées', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200' },
  { statut: 'Annulée', label: 'Annulées', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200' },
  { statut: 'À reprogrammer', label: 'À reprogrammer', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200' },
];

const prioriteColors: Record<string, string> = {
  'Haute': 'bg-red-100 text-red-700',
  'Normale': 'bg-slate-100 text-slate-600',
  'Basse': 'bg-slate-50 text-slate-400',
};

interface VisiteFormData {
  local: string;
  localRef: string;
  enseigne: string;
  contact: string;
  date: string;
  heure: string;
  broker: string;
  priorite: string;
  note: string;
}

const defaultForm: VisiteFormData = {
  local: '', localRef: '', enseigne: '', contact: '', date: '', heure: '10:00',
  broker: 'K. Alami', priorite: 'Normale', note: '',
};

export default function VisitesContent() {
  const [visites, setVisites] = useState<Visite[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<VisiteFormData>(defaultForm);
  const [dragOver, setDragOver] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [locauxOptions, setLocauxOptions] = useState<{ ref: string; title: string }[]>([]);
  const [enseignesOptions, setEnseignesOptions] = useState<string[]>([]);

  useEffect(() => {
    Promise.all([
      visitesService.getAll(),
      locauxService.getAll(),
      enseignesService.getAll(),
    ]).then(([visitesData, locauxData, enseignesData]) => {
      setVisites(visitesData);
      setLocauxOptions(locauxData.map(l => ({ ref: l.ref, title: l.title })));
      setEnseignesOptions(enseignesData.map(e => e.nom));
      setLoading(false);
    });
  }, []);

  const setField = (key: keyof VisiteFormData, value: string) => setForm(f => ({ ...f, [key]: value }));

  const handleLocalChange = (titleAndRef: string) => {
    // Format: "REF — Title"
    const local = locauxOptions.find(l => `${l.ref} — ${l.title}` === titleAndRef);
    if (local) {
      setForm(f => ({ ...f, local: local.title, localRef: local.ref }));
    } else {
      setForm(f => ({ ...f, local: titleAndRef, localRef: '' }));
    }
  };

  const openCreate = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowModal(true);
  };

  const openEdit = (v: Visite) => {
    setForm({
      local: v.local, localRef: v.localRef, enseigne: v.enseigne, contact: v.contact,
      date: v.date, heure: v.heure, broker: v.broker,
      priorite: v.priorite, note: v.note,
    });
    setEditingId(v.id);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.local || !form.enseigne || !form.date) {
      toast.error('Local, enseigne et date sont obligatoires');
      return;
    }
    setSaving(true);
    if (editingId) {
      const updated = await visitesService.update(editingId, {
        local: form.local,
        localRef: form.localRef,
        enseigne: form.enseigne,
        contact: form.contact,
        date: form.date,
        heure: form.heure,
        broker: form.broker,
        priorite: form.priorite as Visite['priorite'],
        note: form.note,
      });
      if (updated) {
        setVisites(prev => prev.map(v => v.id === editingId ? updated : v));
        toast.success('Visite mise à jour !');
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } else {
      const created = await visitesService.create({
        local: form.local,
        localRef: form.localRef,
        enseigne: form.enseigne,
        contact: form.contact,
        date: form.date,
        heure: form.heure,
        broker: form.broker,
        priorite: form.priorite,
        note: form.note,
      });
      if (created) {
        setVisites(prev => [created, ...prev]);
        toast.success('Visite planifiée avec succès !');
      } else {
        toast.error('Erreur lors de la création');
      }
    }
    setSaving(false);
    setShowModal(false);
  };

  const handleDelete = async (id: string) => {
    const visite = visites.find(v => v.id === id);
    const ok = await visitesService.delete(id);
    if (ok) {
      setVisites(prev => prev.filter(v => v.id !== id));
      toast.success(`Visite "${visite?.enseigne}" supprimée`);
    } else {
      toast.error('Erreur lors de la suppression');
    }
    setDeleteConfirmId(null);
  };

  const moveVisite = async (id: string, newStatut: Visite['statut']) => {
    const updated = await visitesService.update(id, { statut: newStatut });
    if (updated) {
      setVisites(vs => vs.map(v => v.id === id ? { ...v, statut: newStatut } : v));
      toast.success(`Visite déplacée vers "${newStatut}"`);
    }
  };

  const inputCls = 'input-field';
  const labelCls = 'block text-xs font-600 text-slate-600 mb-1.5 uppercase tracking-wide';
  const visiteToDelete = visites.find(v => v.id === deleteConfirmId);

  return (
    <div className="px-6 lg:px-8 xl:px-10 py-7 max-w-screen-2xl mx-auto">

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-700 text-slate-900">Visites & Pipeline</h1>
          <p className="text-sm text-slate-500 mt-0.5">{loading ? 'Chargement...' : `${visites.length} visite${visites.length !== 1 ? 's' : ''} au total · Vue Kanban`}</p>
        </div>
        <button onClick={openCreate} className="btn-primary">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Planifier une visite
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <svg className="w-8 h-8 animate-spin text-primary-700" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {COLUMNS.map(col => {
              const count = visites.filter(v => v.statut === col.statut).length;
              return (
                <div key={col.statut} className={`rounded-xl border p-4 ${col.bg} ${col.border}`}>
                  <p className={`text-2xl font-700 ${col.color}`}>{count}</p>
                  <p className={`text-xs font-600 ${col.color} mt-0.5`}>{col.label}</p>
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {COLUMNS.map(col => {
              const colVisites = visites.filter(v => v.statut === col.statut);
              return (
                <div
                  key={col.statut}
                  className={`rounded-xl border-2 transition-colors ${dragOver === col.statut ? `${col.border} ${col.bg}` : 'border-slate-200 bg-slate-50'}`}
                  onDragOver={e => { e.preventDefault(); setDragOver(col.statut); }}
                  onDragLeave={() => setDragOver(null)}
                  onDrop={e => { e.preventDefault(); const id = e.dataTransfer.getData('visiteId'); if (id) moveVisite(id, col.statut); setDragOver(null); }}
                >
                  <div className={`flex items-center justify-between px-4 py-3 rounded-t-xl border-b ${col.border} ${col.bg}`}>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-700 ${col.color}`}>{col.label}</span>
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-700 ${col.bg} ${col.color} border ${col.border}`}>{colVisites.length}</span>
                    </div>
                  </div>
                  <div className="p-3 space-y-3 min-h-48">
                    {colVisites.map(v => (
                      <div key={v.id} draggable onDragStart={e => e.dataTransfer.setData('visiteId', v.id)} className="bg-white rounded-xl border border-slate-200 p-3.5 shadow-sm cursor-grab active:cursor-grabbing hover:shadow-card-hover transition-all duration-150 group">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <p className="text-xs font-700 text-slate-900 leading-tight">{v.enseigne}</p>
                          <span className={`text-[10px] font-600 px-1.5 py-0.5 rounded-full flex-shrink-0 ${prioriteColors[v.priorite]}`}>{v.priorite}</span>
                        </div>
                        <p className="text-xs text-slate-500 truncate mb-2">{v.local}</p>
                        <p className="text-[10px] font-500 text-slate-400 font-mono mb-2">{v.localRef}</p>
                        <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-2">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                          <span>{v.date} à {v.heure}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-slate-500">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          <span>{v.contact}</span>
                        </div>
                        {v.note && <p className="text-xs text-slate-500 bg-slate-50 rounded-lg px-2 py-1.5 mt-2 italic">{v.note}</p>}
                        <div className="flex items-center gap-1 mt-3 pt-2 border-t border-slate-100 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => openEdit(v)} className="p-1 rounded-md hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-700" title="Modifier">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                          <button onClick={() => setDeleteConfirmId(v.id)} className="p-1 rounded-md hover:bg-red-50 transition-colors text-slate-400 hover:text-red-500" title="Supprimer">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                          <div className="flex-1 flex gap-1">
                            {COLUMNS.filter(c => c.statut !== col.statut).slice(0, 2).map(c => (
                              <button key={c.statut} onClick={() => moveVisite(v.id, c.statut)} className={`flex-1 text-[10px] font-600 py-1 rounded-md transition-colors ${c.bg} ${c.color} hover:opacity-80`}>
                                → {c.label.replace('ées', '').replace('ée', '')}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                    {colVisites.length === 0 && (
                      <div className="flex items-center justify-center h-24 text-xs text-slate-300 italic">Aucune visite</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirmId && visiteToDelete && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-modal border border-slate-200 p-6 max-w-md w-full">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </div>
              <div>
                <h3 className="text-base font-700 text-slate-900 mb-1">Supprimer cette visite ?</h3>
                <p className="text-sm text-slate-500 leading-relaxed">La visite <span className="font-600 text-slate-700">"{visiteToDelete.enseigne}"</span> du {visiteToDelete.date} sera supprimée.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 mt-6">
              <button onClick={() => setDeleteConfirmId(null)} className="px-4 py-2 text-sm font-600 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">Annuler</button>
              <button onClick={() => handleDelete(deleteConfirmId)} className="px-4 py-2 text-sm font-600 text-white bg-red-600 rounded-lg hover:bg-red-700 active:scale-95 transition-all">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-modal w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-slate-200">
              <h2 className="text-lg font-700 text-slate-900">{editingId ? 'Modifier la visite' : 'Planifier une visite'}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className={labelCls}>Local commercial <span className="text-red-500">*</span></label>
                <select
                  className={inputCls}
                  value={form.local && form.localRef ? `${form.localRef} — ${form.local}` : ''}
                  onChange={e => handleLocalChange(e.target.value)}
                >
                  <option value="">— Sélectionner un local —</option>
                  {locauxOptions.map(l => (
                    <option key={l.ref} value={`${l.ref} — ${l.title}`}>{l.ref} — {l.title}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Enseigne <span className="text-red-500">*</span></label>
                <select className={inputCls} value={form.enseigne} onChange={e => setField('enseigne', e.target.value)}>
                  <option value="">— Sélectionner une enseigne —</option>
                  {enseignesOptions.map(e => <option key={e}>{e}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Contact enseigne</label>
                <input type="text" className={inputCls} placeholder="Nom, prénom" value={form.contact} onChange={e => setField('contact', e.target.value)} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Date <span className="text-red-500">*</span></label>
                  <input type="date" className={inputCls} value={form.date} onChange={e => setField('date', e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Heure</label>
                  <input type="time" className={inputCls} value={form.heure} onChange={e => setField('heure', e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Broker</label>
                  <select className={inputCls} value={form.broker} onChange={e => setField('broker', e.target.value)}>
                    {['K. Alami', 'S. Benali', 'N. Tahiri', 'M. Ouali'].map(b => <option key={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Priorité</label>
                  <select className={inputCls} value={form.priorite} onChange={e => setField('priorite', e.target.value)}>
                    {['Haute', 'Normale', 'Basse'].map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>Notes préparatoires</label>
                <textarea className={inputCls} rows={3} placeholder="Instructions, documents à apporter..." value={form.note} onChange={e => setField('note', e.target.value)} />
              </div>
            </div>
            <div className="flex items-center justify-between p-6 border-t border-slate-200">
              <button onClick={() => setShowModal(false)} className="btn-secondary">Annuler</button>
              <button onClick={handleSave} disabled={saving} className="btn-primary disabled:opacity-60">
                {saving ? (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                )}
                {editingId ? 'Sauvegarder' : 'Planifier la visite'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
